import React, { useState } from "react";

import motionify from "../../components/Motion";
import { Form, FormikProvider, useFormik } from "formik";
import dayjs from 'dayjs';

import * as Yup from "yup";
import CurrentCommercialPlan from "../../components/CurrentCommercialPlan";
import TailwindInput from "../../components/Tailwind/TailwindInput";
import TailwindButton from "../../components/Tailwind/TailwindButton";
import { cn } from "../../lib/utils";
import { useCurrentUser } from "../../shared/hooks/queries/useCurrentUser";
import { useChangePassword } from "../../shared/hooks/mutations/useChangePassword";
import { ApiError } from "../../shared/api/user";

const ResetPassword = () => {
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);
    const [passwordError, setPasswordError] = useState({
        message: '',
        variant: ''
    });

    const changePasswordMutation = useChangePassword();

    const SignupSchema = Yup.object().shape({
        current_password: Yup.string()
            .required("Current password is required"),
        password: Yup.string()
            .required("New password is required"),
        password_confirmation: Yup.string()
            .required("New password confirmation is required")
    });

    const formik = useFormik({
        initialValues: {
            current_password: "",
            password: "",
            password_confirmation: "",
        },
        validationSchema: SignupSchema,
        onSubmit: () => {
            if (values.password == values.password_confirmation) {
                const passwordValidation = new RegExp(
                    /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,}$/
                );
                const isValidPassword = passwordValidation.test(values.password);
                if (isValidPassword) {
                    setPasswordError({ message: '', variant: '' });
                    changePasswordMutation.mutate(
                        {
                            currentPassword: values.current_password,
                            newPassword: values.password,
                        },
                        {
                            onSuccess: () => {
                                setSubmitting(false);
                                setPasswordError({
                                    message: 'Your password has been changed',
                                    variant: 'success'
                                });
                                resetForm({ values: { current_password: '', password: '', password_confirmation: '' } });
                            },
                            onError: (error) => {
                                setSubmitting(false);
                                const detail = error instanceof ApiError && error.body
                                    ? (error.body as { detail?: string }).detail
                                    : undefined;
                                setPasswordError({
                                    message: detail || 'An error occurred while changing your password',
                                    variant: 'error'
                                });
                            },
                        },
                    );
                } else {
                    setSubmitting(false);
                    setPasswordError({
                        message: 'The new password must contain 8 characters with at least one lowercase, one uppercase, one digit and one special character',
                        variant: 'error'
                    });
                }
            } else {
                setSubmitting(false);
                setPasswordError({
                    message: "The new password and the new password confirmation doesn't match",
                    variant: 'error'
                });
            }
        },
    });

    const { errors, touched, values, handleSubmit, isSubmitting, getFieldProps, setSubmitting, setFieldValue, resetForm } = formik;

    const clearError = () => setPasswordError({ message: '', variant: '' });

    const PasswordField = ({ label, fieldName, show, setShow }: { label: string; fieldName: string; show: boolean; setShow: (v: boolean) => void }) => (
        <div className="relative">
            <TailwindInput
                fullWidth
                type={show ? "text" : "password"}
                label={label}
                {...getFieldProps(fieldName)}
                error={touched[fieldName] && errors[fieldName] ? errors[fieldName] : undefined}
                onChange={(e) => {
                    setFieldValue(fieldName, e.target.value);
                    clearError();
                }}
            />
            <button
                type="button"
                className="absolute right-3 top-9 text-slate-400 hover:text-slate-600 transition-colors"
                onClick={() => setShow(!show)}
            >
                <span className="material-symbols-outlined text-[20px]">
                    {show ? 'visibility' : 'visibility_off'}
                </span>
            </button>
        </div>
    );

    return (
        <FormikProvider value={formik}>
            <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
                <div className="flex flex-col gap-5 max-w-sm">
                    <PasswordField label="Current password" fieldName="current_password" show={showCurrentPassword} setShow={setShowCurrentPassword} />
                    <PasswordField label="New password" fieldName="password" show={showPassword} setShow={setShowPassword} />
                    <PasswordField label="Confirm new password" fieldName="password_confirmation" show={showPasswordConfirmation} setShow={setShowPasswordConfirmation} />
                </div>

                {passwordError.message && (
                    <div className={cn(
                        "mt-4 max-w-sm flex items-center gap-2 p-3 rounded-lg text-sm",
                        passwordError.variant === 'success'
                            ? "bg-green-50 text-green-800 border border-green-200"
                            : "bg-red-50 text-red-800 border border-red-200"
                    )}>
                        <span className="material-symbols-outlined text-[18px] shrink-0">
                            {passwordError.variant === 'success' ? 'check_circle' : 'error'}
                        </span>
                        {passwordError.message}
                    </div>
                )}

                <TailwindButton
                    size="md"
                    type="submit"
                    variant="dark"
                    icon="lock_reset"
                    disabled={isSubmitting}
                    className="mt-6"
                >
                    {isSubmitting ? 'Changing...' : 'Change password'}
                </TailwindButton>
            </Form>
        </FormikProvider>
    );
};

export default function MyAccount({ entity }) {
    entity;

    const { data: user } = useCurrentUser();

    const firstName = user?.first_name ?? '';
    const lastName = user?.last_name ?? '';
    const isSsoOnly = user?.sso_only ?? false;
    const email = user?.email ?? '';
    const lastConnection = user?.last_connection
        ? dayjs(user.last_connection).format('DD/MM/YYYY HH:mm:ss')
        : '';

    const getInitials = () => {
        const f = firstName?.[0] || '';
        const l = lastName?.[0] || '';
        return (f + l).toUpperCase() || '?';
    };

    const handleClick = (event, url) => {
        event.preventDefault();
        window.open(url, '_blank');
    };

    return (
        motionify(
            <div className="flex-grow bg-white min-h-full">
                {/* Page Header */}
                <div className="px-8 py-6 border-b border-slate-100 bg-white sticky top-0 z-10">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                        <div className="flex-1">
                            <h2 className="text-3xl font-black text-slate-900 tracking-tight">
                                My Account
                            </h2>
                            <p className="text-sm text-slate-500 mt-2">
                                Manage your profile, subscription, and security settings
                            </p>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="px-8 py-6 flex flex-col gap-6">
                    {/* Profile Card */}
                    <div className="border border-slate-100 rounded-xl overflow-hidden shadow-sm">
                        <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center gap-2">
                            <span className="material-symbols-outlined text-[20px] text-slate-600">person</span>
                            <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Profile</h3>
                        </div>
                        <div className="p-6">
                            <div className="flex items-start gap-6">
                                {/* Avatar */}
                                <div className="size-16 rounded-xl bg-primary text-white flex items-center justify-center text-xl font-bold shrink-0 shadow-md shadow-primary/20">
                                    {getInitials()}
                                </div>
                                {/* Info Grid */}
                                <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
                                    <div>
                                        <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">First name</span>
                                        <p className="text-sm font-medium text-slate-900 mt-0.5">{firstName || <span className="text-slate-300">-</span>}</p>
                                    </div>
                                    <div>
                                        <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Last name</span>
                                        <p className="text-sm font-medium text-slate-900 mt-0.5">{lastName || <span className="text-slate-300">-</span>}</p>
                                    </div>
                                    <div>
                                        <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Email</span>
                                        <p className="text-sm font-medium text-slate-900 mt-0.5">{email || <span className="text-slate-300">-</span>}</p>
                                    </div>
                                    <div>
                                        <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Last connection</span>
                                        <p className="text-sm font-medium text-slate-900 mt-0.5">{lastConnection || <span className="text-slate-300">-</span>}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Commercial Plan Card */}
                    <div className="border border-slate-100 rounded-xl overflow-hidden shadow-sm">
                        <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center gap-2">
                            <span className="material-symbols-outlined text-[20px] text-slate-600">workspace_premium</span>
                            <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Commercial Plan</h3>
                        </div>
                        <div className="p-6">
                            <CurrentCommercialPlan />
                        </div>
                    </div>

                    {/* Change Password Card */}
                    {!isSsoOnly && (
                        <div className="border border-slate-100 rounded-xl overflow-hidden shadow-sm">
                            <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center gap-2">
                                <span className="material-symbols-outlined text-[20px] text-slate-600">lock</span>
                                <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Change Password</h3>
                            </div>
                            <div className="p-6">
                                <ResetPassword />
                            </div>
                        </div>
                    )}

                    {/* Legal Card */}
                    <div className="border border-slate-100 rounded-xl overflow-hidden shadow-sm">
                        <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center gap-2">
                            <span className="material-symbols-outlined text-[20px] text-slate-600">gavel</span>
                            <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Legal</h3>
                        </div>
                        <div className="p-6">
                            <div className="flex flex-col gap-3">
                                <a
                                    href="https://app.peerception.io/documents/CG.pdf"
                                    onClick={(e) => handleClick(e, 'https://app.peerception.io/documents/CG.pdf')}
                                    className="flex items-center gap-3 p-3 rounded-lg border border-slate-100 hover:border-primary hover:bg-primary/5 transition-colors group"
                                >
                                    <span className="material-symbols-outlined text-[20px] text-slate-400 group-hover:text-primary transition-colors">description</span>
                                    <div className="flex-1">
                                        <p className="text-sm font-medium text-slate-900 group-hover:text-primary transition-colors">General Terms of Use</p>
                                        <p className="text-[11px] text-slate-400">PDF document</p>
                                    </div>
                                    <span className="material-symbols-outlined text-[18px] text-slate-300 group-hover:text-primary transition-colors">open_in_new</span>
                                </a>
                                <a
                                    href="https://app.peerception.io/documents/Politique_de_Confidentialite.pdf"
                                    onClick={(e) => handleClick(e, 'https://app.peerception.io/documents/Politique_de_Confidentialite.pdf')}
                                    className="flex items-center gap-3 p-3 rounded-lg border border-slate-100 hover:border-primary hover:bg-primary/5 transition-colors group"
                                >
                                    <span className="material-symbols-outlined text-[20px] text-slate-400 group-hover:text-primary transition-colors">shield</span>
                                    <div className="flex-1">
                                        <p className="text-sm font-medium text-slate-900 group-hover:text-primary transition-colors">Privacy Policy</p>
                                        <p className="text-[11px] text-slate-400">PDF document</p>
                                    </div>
                                    <span className="material-symbols-outlined text-[18px] text-slate-300 group-hover:text-primary transition-colors">open_in_new</span>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        )
    );
}
